import _ from "lodash";
import React, { useEffect, useState } from "react"
import { useLazyGetReminderTaskQuery, useLazySearchReminderTasksQuery } from "../../../services/reminders";
import { useFilterContext } from "../../../hooks/FilterContext";

const PersonalRemindersContext = React.createContext();

export const usePersonalReminders = () => React.useContext(PersonalRemindersContext);

const PersonalRemindersProvider = ({ children, initSort = "deadlineDate+DESC", initLimit = 50 }) => {

    const { filter, isReady } = useFilterContext();

    const [cacheKey, setCacheKey] = React.useState(new Date().valueOf());
    const [sort, setSort] = React.useState(initSort);
    const [limit, setLimit] = React.useState(initLimit);

    const [selectedReminderId, setSelectedReminderId] = useState(null);

    const [search, { data, isLoading, isFetching, isError, error, isUninitialized }] = useLazySearchReminderTasksQuery();

    const { pagination, results } = data || { pagination: { page: 1, pageSize: limit, totalCount: 0, totalPages: 1 }, results: [] };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = totalCount > 0 && page < totalPages;

    const [getReminder, getReminderResults] = useLazyGetReminderTaskQuery();
    const { currentData: selectedReminder, isFetching: isFetchingReminder, error: reminderError } = getReminderResults || {};
    
    const isNextPageLoading = isFetching === true || isLoading === true;

    useEffect(() => {
        if (!selectedReminderId) return;

        getReminder({ reminderId: selectedReminderId });
    }, [getReminder, selectedReminderId]);

    const clear = (newSort) => {
         // safety check to make sure we don't reload when already loading!
         if (isNextPageLoading || !isReady) {
            return;
        }

        const newCacheKey = new Date().valueOf();

        search({
           ...filter,
            cacheKey: newCacheKey,
            sort,
            limit,
            page: 1,
        })
        .unwrap()
        .then(_ => {
            setCacheKey(newCacheKey);
                
        });
    };

    const reload = (e) => {
        if (e && typeof e.preventDefault === "function") {
            e.preventDefault();
        }

        clear();
    };

    const isRowLoaded = (index) => !hasMore || index < results.length;

    const loadMore = () => {
        // safety check to make sure we don't reload when already loading!
        if (isNextPageLoading) {
            return;
        }

       search({
            cacheKey,
            sort,
            limit,
            page: page + 1,
            ...filter,
        });
    };

    const sortTable = (property) => {
        // we want the sort to initially be descending ??
        let newDirection = "";
        let [sortProperty, direction] = sort.split("+");

        // if the current property is already selected, reverse the sort direction
        if (property === sortProperty) {
            newDirection = direction === "DESC" ? null : "+DESC";
        }

        setSort(property + (newDirection ?? ""));
    };

    React.useEffect(clear, [filter, sort, isReady]);
        
    return <PersonalRemindersContext.Provider value={{
        results,
        sort,
        totalCount,
        isError,
        error,
        isNextPageLoading,
        clear,
        isRowLoaded,
        loadMore,
        sortTable,
        reload,
        selectedReminder: selectedReminderId ? selectedReminder : null,
        setSelectedReminderId,
        isFetchingReminder,
        reminderError,
    }}>
        {children}
    </PersonalRemindersContext.Provider>
};

export default PersonalRemindersProvider;